/**
 * Diálogo genérico de importación masiva de maestros desde Excel/CSV.
 * Sin IA y sin consumo de créditos: todo el parseo ocurre en el navegador.
 * Solo se muestra a usuarios administradores.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2, Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  MASTER_SCHEMAS, parseWorkbook, indexToColumnLetter, buildRows, importMasterRows,
  buildRejectedCsv, type MasterKind, type BuiltRow, type ImportSummary,
} from "@/lib/masterImport";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: MasterKind;
  companyId: string;
  companyName?: string;
  /** Longitud estándar de cuenta deducida del maestro actual. */
  accountLength?: number;
  onImported?: () => void;
}

const MasterImportDialog = ({
  open, onOpenChange, kind, companyId, companyName, accountLength = 8, onImported,
}: Props) => {
  const { toast } = useToast();
  const schema = MASTER_SCHEMAS[kind];
  const fileRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState("");
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [sheets, setSheets] = useState<unknown[][][]>([]);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [headerRow, setHeaderRow] = useState(1);
  const [mapping, setMapping] = useState<Record<string, number | null>>({});
  const [onExisting, setOnExisting] = useState<"update" | "skip">("update");
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);

  const grid = useMemo<unknown[][]>(() => sheets[sheetIndex] || [], [sheets, sheetIndex]);

  const reset = () => {
    setFileName(""); setSheetNames([]); setSheets([]); setSheetIndex(0);
    setHeaderRow(1); setMapping({}); setSummary(null); setProgress(null);
  };

  useEffect(() => { if (!open) reset(); }, [open]);

  const headers = useMemo<string[]>(() => {
    const row = grid[headerRow - 1] || [];
    return row.map((c) => (c == null ? "" : String(c).trim()));
  }, [grid, headerRow]);

  const columnMeta = useMemo(() => {
    return headers.map((h, i) => {
      let firstValue = "";
      for (let r = headerRow; r < grid.length; r++) {
        const v = grid[r]?.[i];
        if (v != null && v !== "") { firstValue = String(v).slice(0, 24); break; }
      }
      return { index: i, letter: indexToColumnLetter(i), header: h, firstValue, isEmpty: !h && !firstValue };
    }).filter((c) => !c.isEmpty);
  }, [headers, grid, headerRow]);

  // Auto-mapeo por nombre de cabecera
  useEffect(() => {
    if (headers.length === 0) return;
    const next: Record<string, number | null> = {};
    const used = new Set<number>();
    for (const f of schema.fields) {
      let found: number | null = null;
      for (const p of f.patterns) {
        const idx = headers.findIndex((h, i) => h && !used.has(i) && p.test(h));
        if (idx >= 0) { found = idx; break; }
      }
      if (found != null) used.add(found);
      next[f.key] = found;
    }
    setMapping(next);
  }, [headers, kind]);

  const handleFile = async (f: File) => {
    setSummary(null);
    setFileName(f.name);
    try {
      const parsed = await parseWorkbook(f);
      setSheets(parsed.sheets);
      setSheetNames(parsed.sheetNames);
      setSheetIndex(0);
      setHeaderRow(1);
    } catch (e) {
      toast({ title: "Error leyendo el archivo", description: (e as Error).message, variant: "destructive" });
      reset();
    }
  };

  const rows = useMemo<BuiltRow[]>(
    () => (grid.length ? buildRows(grid, headerRow, mapping, schema, accountLength) : []),
    [grid, headerRow, mapping, schema, accountLength],
  );
  const validRows = useMemo(() => rows.filter((r) => r.errors.length === 0), [rows]);
  const invalidRows = useMemo(() => rows.filter((r) => r.errors.length > 0), [rows]);
  const warningRows = useMemo(() => rows.filter((r) => r.errors.length === 0 && r.warnings.length > 0), [rows]);


  const missingRequired = schema.fields.filter((f) => f.required && mapping[f.key] == null).map((f) => f.label);
  const canImport = !!companyId && validRows.length > 0 && missingRequired.length === 0 && !importing;

  const doImport = async () => {
    setImporting(true);
    setProgress({ done: 0, total: validRows.length });
    try {
      const res = await importMasterRows(schema, companyId, rows, onExisting, (done, total) =>
        setProgress({ done, total }),
      );
      setSummary(res);
      onImported?.();
      toast({
        title: "Importación completada",
        description: `${res.created} nuevos · ${res.updated} actualizados · ${res.skipped} omitidos`,
      });
    } catch (e) {
      toast({ title: "Error importando", description: (e as Error).message, variant: "destructive" });
    } finally {
      setImporting(false);
      setProgress(null);
    }
  };

  const downloadRejected = () => {
    const csv = buildRejectedCsv(schema, invalidRows);
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `filas-rechazadas-${schema.label}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar {schema.label} desde Excel</DialogTitle>
          <DialogDescription>
            Carga masiva sin consumo de créditos{companyName ? ` · ${companyName}` : ""}.
            Se procesa en tu navegador y se guarda directamente en el maestro.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Archivo */}
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ""; }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full rounded-lg border border-dashed p-6 text-center transition-colors hover:border-[hsl(var(--brand-green))] hover:bg-accent/40"
            >
              {fileName ? (
                <span className="inline-flex items-center gap-2 text-sm font-medium">
                  <FileSpreadsheet className="h-4 w-4 text-[hsl(var(--brand-green))]" /> {fileName}
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                  <Upload className="h-4 w-4" /> Selecciona un archivo .xlsx o .csv
                </span>
              )}
            </button>
          </div>

          {grid.length > 0 && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <Label className="text-xs">Hoja</Label>
                  <Select value={String(sheetIndex)} onValueChange={(v) => setSheetIndex(Number(v))}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {sheetNames.map((n, i) => (
                        <SelectItem key={n + i} value={String(i)} className="text-xs">{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Fila de cabecera</Label>
                  <Input
                    type="number"
                    min={1}
                    value={headerRow}
                    onChange={(e) => setHeaderRow(Math.max(1, Number(e.target.value) || 1))}
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs">Si el registro ya existe</Label>
                  <Select value={onExisting} onValueChange={(v) => setOnExisting(v as "update" | "skip")}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="update" className="text-xs">Actualizar sus datos</SelectItem>
                      <SelectItem value="skip" className="text-xs">Omitirlo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Mapeo de columnas */}
              <div className="rounded-lg border p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Emparejar columnas
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {schema.fields.map((f) => (
                    <div key={f.key}>
                      <Label className="text-xs">
                        {f.label} {f.required && <span className="text-destructive">*</span>}
                      </Label>
                      <Select
                        value={mapping[f.key] == null ? "none" : String(mapping[f.key])}
                        onValueChange={(v) => setMapping((m) => ({ ...m, [f.key]: v === "none" ? null : Number(v) }))}
                      >
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none" className="text-xs">— Sin asignar —</SelectItem>
                          {columnMeta.map((c) => (
                            <SelectItem key={c.index} value={String(c.index)} className="text-xs">
                              {c.letter} · {c.header || "(sin título)"}{c.firstValue ? ` — ${c.firstValue}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                {missingRequired.length > 0 && (
                  <p className="flex items-center gap-2 text-xs text-destructive">
                    <AlertCircle className="h-3.5 w-3.5" />
                    Faltan columnas obligatorias: {missingRequired.join(", ")}
                  </p>
                )}
              </div>

              {/* Vista previa */}
              <div className="space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Vista previa</p>
                  <span className="inline-flex items-center gap-1 rounded bg-[hsl(var(--brand-green))]/15 px-2 py-0.5 text-xs font-semibold text-[hsl(var(--brand-green))]">
                    <CheckCircle2 className="h-3 w-3" /> {validRows.length} válidas
                  </span>
                  {warningRows.length > 0 && (
                    <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-2 py-0.5 text-xs font-semibold text-amber-600 dark:text-amber-400">
                      <AlertCircle className="h-3 w-3" /> {warningRows.length} con cuenta repetida (se importan)
                    </span>
                  )}
                  {invalidRows.length > 0 && (
                    <>
                      <span className="inline-flex items-center gap-1 rounded bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive">
                        <AlertCircle className="h-3 w-3" /> {invalidRows.length} con error
                      </span>
                      <Button variant="ghost" size="sm" className="h-6 gap-1 text-xs" onClick={downloadRejected}>
                        <Download className="h-3 w-3" /> Descargar rechazadas
                      </Button>
                    </>
                  )}

                </div>
                <div className="rounded-lg border overflow-x-auto max-h-64 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs w-14">Fila</TableHead>
                        {schema.fields.map((f) => (
                          <TableHead key={f.key} className="text-xs whitespace-nowrap">{f.label}</TableHead>
                        ))}
                        <TableHead className="text-xs">Aviso</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.slice(0, 50).map((r) => (
                        <TableRow key={r.index} className={r.errors.length ? "bg-destructive/5" : r.warnings.length ? "bg-amber-500/10" : undefined}>
                          <TableCell className="text-xs text-muted-foreground">{r.index}</TableCell>
                          {schema.fields.map((f) => (
                            <TableCell key={f.key} className="text-xs font-mono whitespace-nowrap">
                              {r.values[f.key] || <span className="text-muted-foreground/50">—</span>}
                            </TableCell>
                          ))}
                          <TableCell className="text-xs">
                            {r.errors.length > 0 && <span className="text-destructive">{r.errors.join(" · ")}</span>}
                            {r.errors.length > 0 && r.warnings.length > 0 && " · "}
                            {r.warnings.length > 0 && <span className="text-amber-600 dark:text-amber-400">{r.warnings.join(" · ")}</span>}
                          </TableCell>
                        </TableRow>
                      ))}

                      {rows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={schema.fields.length + 2} className="py-6 text-center text-sm text-muted-foreground">
                            No se han detectado filas con los campos obligatorios.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                {rows.length > 50 && (
                  <p className="text-xs text-muted-foreground">Mostrando las primeras 50 de {rows.length} filas.</p>
                )}
              </div>
            </>
          )}

          {summary && (
            <div className="rounded-lg border bg-accent/30 p-4 text-sm space-y-1">
              <p className="font-semibold">Resultado</p>
              <p className="text-xs text-muted-foreground">
                {summary.created} creados · {summary.updated} actualizados · {summary.skipped} omitidos · {summary.failed} con error
              </p>
              {summary.errors.length > 0 && (
                <p className="text-xs text-destructive">{summary.errors.join(" · ")}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={importing}>
            {summary ? "Cerrar" : "Cancelar"}
          </Button>
          <Button
            onClick={doImport}
            disabled={!canImport}
            className="gap-2 bg-[hsl(var(--brand-green))] hover:bg-[hsl(152,40%,45%)] text-white"
          >
            {importing && <Loader2 className="h-4 w-4 animate-spin" />}
            {importing && progress
              ? `Importando ${progress.done}/${progress.total}…`
              : `Importar ${validRows.length} registro${validRows.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MasterImportDialog;
